import { createElement, useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { DateFormatType, SupportedLocale, TimeFormatType } from '@/lib/i18n/config'
import type { NotificationPreferences } from '@/lib/types/preferences'
import { usePageLayout } from '@/hooks/usePageLayout'
import { Switch } from '@/components/ui/switch'
import SettingsSidebar from '@/components/app/sidebars/settings'
import {
  SettingsCard,
  SettingsPage,
  SettingsPageLoading,
  SettingsRow,
  SettingsSaveBar,
  SettingsSection,
  settingsSelectClass,
} from '@/components/app/settings'
import useUserPreferencesStore from '@/store/UserPreferencesStore'
import { getProfile, updateProfile } from '@/lib/api/features/profile'
import {
  defaultNotificationPreferences,
  parseNotificationPreferences,
} from '@/lib/types/preferences'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

// Form validation schema
const preferencesFormSchema = z.object({
  language: z.enum(['en-US', 'en-GB']),
  dateFormat: z.enum(['MM/dd/yyyy', 'dd/MM/yyyy', 'yyyy-MM-dd']),
  timeFormat: z.enum(['12h', '24h']),
  weekStartsOn: z.number().min(0).max(6),
  timezone: z.string(),
  // Notification preferences. Email/Push are now per-event-only and have no
  // master switch (see /settings/notifications). App is the sole remaining master gate.
  enableAppNotifications: z.boolean(),
})

type PreferencesFormData = z.infer<typeof preferencesFormSchema>

const themeSchema = z.enum(['system', 'light', 'dark'])

export default function PreferencesSettingsPage() {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true)

  // Hold the loaded notification JSON so the App-toggle save preserves
  // per-event prefs / digest / dismissedNotices owned by NotificationSettingsPage.
  const [loadedNotificationPrefs, setLoadedNotificationPrefs] =
    useState<NotificationPreferences>(defaultNotificationPreferences)

  // Get current preferences from store
  const currentLanguage = useUserPreferencesStore((state) => state.language)
  const currentDateFormat = useUserPreferencesStore((state) => state.dateFormat)
  const currentTimeFormat = useUserPreferencesStore((state) => state.timeFormat)
  const currentWeekStartsOn = useUserPreferencesStore((state) => state.weekStartsOn)
  const currentTimezone = useUserPreferencesStore((state) => state.timezone)
  // Theme is device-local and applies immediately, outside the profile form.
  const currentTheme = useUserPreferencesStore((state) => state.theme)
  const setTheme = useUserPreferencesStore((state) => state.setTheme)

  // Update actions from store
  const updatePreferences = useUserPreferencesStore((state) => state.updatePreferences)

  // Form state with react-hook-form
  const form = useForm<PreferencesFormData>({
    resolver: zodResolver(preferencesFormSchema),
    defaultValues: {
      language: currentLanguage as SupportedLocale,
      dateFormat: currentDateFormat as DateFormatType,
      timeFormat: currentTimeFormat as TimeFormatType,
      weekStartsOn: currentWeekStartsOn,
      timezone: currentTimezone,
      enableAppNotifications: defaultNotificationPreferences.enableAppNotifications,
    },
  })

  // Load preferences from backend
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        setIsLoadingPreferences(true)
        const profileData = await getProfile()

        // Parse notification preferences via the shared canonical parser so
        // legacy fields (enableEmailNotifications, enablePushNotifications,
        // enableSmsNotifications) are dropped silently and missing event keys
        // get their canonical defaults.
        const notificationPrefs = parseNotificationPreferences(profileData.notificationsJson)
        setLoadedNotificationPrefs(notificationPrefs)

        // Update form with backend data
        form.reset({
          language: profileData.language as SupportedLocale,
          dateFormat: profileData.dateFormat as DateFormatType,
          timeFormat: profileData.timeFormat as TimeFormatType,
          weekStartsOn: profileData.weekStartsOn,
          timezone: profileData.timeZone,
          enableAppNotifications: notificationPrefs.enableAppNotifications,
        })

        // Update store
        updatePreferences({
          language: profileData.language as SupportedLocale,
          dateFormat: profileData.dateFormat as DateFormatType,
          timeFormat: profileData.timeFormat as TimeFormatType,
          weekStartsOn: profileData.weekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6,
          timezone: profileData.timeZone,
        })
      } catch (error) {
        logger.logError(
          error as Error,
          LogCategory.API_ERROR,
          {
            component: 'PreferencesSettingsPage',
            action: 'load_preferences',
          }
        )
        toast.error(t('preferencesPage.toast.loadFailed'))
      } finally {
        setIsLoadingPreferences(false)
      }
    }

    loadPreferences()
  }, [])

  const onSubmit = async (data: PreferencesFormData) => {
    try {
      // Prepare notification preferences JSON. Per-event prefs / digest /
      // dismissedNotices are owned by NotificationSettingsPage; merge our
      // App-master change onto the loaded prefs so we don't clobber them.
      const notificationPrefs: NotificationPreferences = {
        ...loadedNotificationPrefs,
        enableAppNotifications: data.enableAppNotifications,
      }

      // Update backend
      await updateProfile({
        language: data.language,
        dateFormat: data.dateFormat,
        timeFormat: data.timeFormat,
        weekStartsOn: data.weekStartsOn,
        timeZone: data.timezone,
        notificationsJson: JSON.stringify(notificationPrefs),
      })

      // Update local store (will trigger i18n change via middleware)
      updatePreferences({
        language: data.language as SupportedLocale,
        dateFormat: data.dateFormat as DateFormatType,
        timeFormat: data.timeFormat as TimeFormatType,
        weekStartsOn: data.weekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6,
        timezone: data.timezone,
      })

      // Saved values become the new baseline, which hides the save bar.
      form.reset(data)
      toast.success(t('preferencesPage.toast.updated'))
    } catch (error) {
      logger.logError(
        error as Error,
        LogCategory.API_ERROR,
        {
          component: 'PreferencesSettingsPage',
          action: 'update_preferences',
        }
      )
      toast.error(t('preferencesPage.toast.updateFailed'))
    }
  }

  if (isLoadingPreferences) {
    return <SettingsPageLoading />
  }

  return (
    <SettingsPage
      title={t('preferencesPage.title')}
      description={t('preferencesPage.description')}
    >
      {/* Appearance — applies instantly, stored on this device only */}
      <SettingsSection title={t('preferencesPage.appearance')}>
        <SettingsCard>
          <SettingsRow
            label={t('preferencesPage.fields.theme')}
            description={t('preferencesPage.fields.themeHint')}
            htmlFor="theme-select"
            control="field"
          >
            <select
              id="theme-select"
              value={currentTheme}
              onChange={(e) => setTheme(themeSchema.parse(e.target.value))}
              className={settingsSelectClass}
            >
              <option value="system">{t('preferencesPage.themes.system')}</option>
              <option value="light">{t('preferencesPage.themes.light')}</option>
              <option value="dark">{t('preferencesPage.themes.dark')}</option>
            </select>
          </SettingsRow>
        </SettingsCard>
      </SettingsSection>

      <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
        {/* Language & Region */}
        <SettingsSection title={t('preferencesPage.languageSectionTitle')}>
          <SettingsCard>
            <SettingsRow
              label={t('preferencesPage.fields.language')}
              description={t('preferencesPage.fields.languageHint')}
              htmlFor="pref-language"
              control="field"
            >
              <select id="pref-language" {...form.register('language')} className={settingsSelectClass}>
                <option value="en-US">{t('preferencesPage.locales.en-US')}</option>
                <option value="en-GB">{t('preferencesPage.locales.en-GB')}</option>
              </select>
            </SettingsRow>

            <SettingsRow
              label={t('preferencesPage.fields.dateFormat')}
              description={t('preferencesPage.fields.dateFormatHint')}
              htmlFor="pref-date-format"
              control="field"
            >
              <select id="pref-date-format" {...form.register('dateFormat')} className={settingsSelectClass}>
                <option value="MM/dd/yyyy">{t('dateFormats.mdy')}</option>
                <option value="dd/MM/yyyy">{t('dateFormats.dmy')}</option>
                <option value="yyyy-MM-dd">{t('dateFormats.ymd')}</option>
              </select>
            </SettingsRow>

            <SettingsRow
              label={t('preferencesPage.fields.timeFormat')}
              description={t('preferencesPage.fields.timeFormatHint')}
              htmlFor="pref-time-format"
              control="field"
            >
              <select id="pref-time-format" {...form.register('timeFormat')} className={settingsSelectClass}>
                <option value="12h">{t('timeFormats.12h')}</option>
                <option value="24h">{t('timeFormats.24h')}</option>
              </select>
            </SettingsRow>

            <SettingsRow
              label={t('preferencesPage.fields.weekStartsOn')}
              description={t('preferencesPage.fields.weekStartsOnHint')}
              htmlFor="pref-week-start"
              control="field"
            >
              <select
                id="pref-week-start"
                {...form.register('weekStartsOn', { valueAsNumber: true })}
                className={settingsSelectClass}
              >
                <option value={0}>{tCommon('days.sunday')}</option>
                <option value={1}>{tCommon('days.monday')}</option>
                <option value={2}>{tCommon('days.tuesday')}</option>
                <option value={3}>{tCommon('days.wednesday')}</option>
                <option value={4}>{tCommon('days.thursday')}</option>
                <option value={5}>{tCommon('days.friday')}</option>
                <option value={6}>{tCommon('days.saturday')}</option>
              </select>
            </SettingsRow>

            <SettingsRow
              label={t('preferencesPage.fields.timezone')}
              description={t('preferencesPage.fields.timezoneHint')}
              htmlFor="pref-timezone"
              control="field"
            >
              <select id="pref-timezone" {...form.register('timezone')} className={settingsSelectClass}>
                <optgroup label={t('preferencesPage.timezoneGroups.america')}>
                  <option value="America/New_York">Eastern (ET)</option>
                  <option value="America/Chicago">Central (CT)</option>
                  <option value="America/Denver">Mountain (MT)</option>
                  <option value="America/Phoenix">Arizona (MST)</option>
                  <option value="America/Los_Angeles">Pacific (PT)</option>
                  <option value="America/Anchorage">Alaska (AKT)</option>
                  <option value="Pacific/Honolulu">Hawaii (HST)</option>
                  <option value="America/Toronto">Toronto</option>
                  <option value="America/Vancouver">Vancouver</option>
                  <option value="America/Mexico_City">Mexico City</option>
                  <option value="America/Sao_Paulo">São Paulo</option>
                  <option value="America/Buenos_Aires">Buenos Aires</option>
                </optgroup>
                <optgroup label={t('preferencesPage.timezoneGroups.europe')}>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="Europe/Paris">Paris (CET)</option>
                  <option value="Europe/Berlin">Berlin (CET)</option>
                  <option value="Europe/Rome">Rome (CET)</option>
                  <option value="Europe/Madrid">Madrid (CET)</option>
                  <option value="Europe/Amsterdam">Amsterdam (CET)</option>
                  <option value="Europe/Brussels">Brussels (CET)</option>
                  <option value="Europe/Zurich">Zurich (CET)</option>
                  <option value="Europe/Stockholm">Stockholm (CET)</option>
                  <option value="Europe/Athens">Athens (EET)</option>
                  <option value="Europe/Istanbul">Istanbul (TRT)</option>
                  <option value="Europe/Moscow">Moscow (MSK)</option>
                </optgroup>
                <optgroup label={t('preferencesPage.timezoneGroups.asia')}>
                  <option value="Asia/Dubai">Dubai (GST)</option>
                  <option value="Asia/Kolkata">India (IST)</option>
                  <option value="Asia/Bangkok">Bangkok (ICT)</option>
                  <option value="Asia/Singapore">Singapore (SGT)</option>
                  <option value="Asia/Hong_Kong">Hong Kong (HKT)</option>
                  <option value="Asia/Shanghai">Shanghai (CST)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Asia/Seoul">Seoul (KST)</option>
                </optgroup>
                <optgroup label={t('preferencesPage.timezoneGroups.pacific')}>
                  <option value="Australia/Sydney">Sydney (AEDT)</option>
                  <option value="Australia/Melbourne">Melbourne (AEDT)</option>
                  <option value="Australia/Brisbane">Brisbane (AEST)</option>
                  <option value="Australia/Perth">Perth (AWST)</option>
                  <option value="Pacific/Auckland">Auckland (NZDT)</option>
                </optgroup>
                <optgroup label={t('preferencesPage.timezoneGroups.africa')}>
                  <option value="Africa/Cairo">Cairo (EET)</option>
                  <option value="Africa/Johannesburg">Johannesburg (SAST)</option>
                  <option value="Africa/Lagos">Lagos (WAT)</option>
                </optgroup>
              </select>
            </SettingsRow>
          </SettingsCard>
        </SettingsSection>

        {/* In-app master switch. Email/Push are per event on the Notifications page. */}
        <SettingsSection
          title={t('preferencesPage.notifications.title')}
          description={t('preferencesPage.notifications.description')}
        >
          <SettingsCard>
            <SettingsRow
              label={t('preferencesPage.fields.appNotifications')}
              description={t('preferencesPage.fields.appNotificationsHint')}
              htmlFor="pref-app-notifications"
            >
              <Switch
                id="pref-app-notifications"
                checked={form.watch('enableAppNotifications')}
                onCheckedChange={(checked) =>
                  form.setValue('enableAppNotifications', checked, { shouldDirty: true })
                }
              />
            </SettingsRow>
          </SettingsCard>
        </SettingsSection>

        <SettingsSaveBar
          visible={form.formState.isDirty}
          isSaving={form.formState.isSubmitting}
          onDiscard={() => form.reset()}
        />
      </form>
    </SettingsPage>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/preferences',
)({
  component: PreferencesSettingsPage,
})
